import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, FileTextIcon } from 'lucide-react';
import { useInvoices } from '@/hooks/useInvoices';
import { useHasInvoiceSettings } from '@/hooks/useInvoiceSettings';
import { InvoiceCard } from '@/components/invoices/InvoiceCard';
import { BottomNav } from '@/components/BottomNav';
import type { Invoice } from '@/types/invoice';

type TabType = 'all' | 'draft' | 'sent' | 'paid';

export function Invoices() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const { hasSettings, isConfigured } = useHasInvoiceSettings();

  // Fetch invoices with filters
  const allQuery = useInvoices();
  const draftQuery = useInvoices({ status: 'draft' });
  const sentQuery = useInvoices({ status: 'sent' });
  const paidQuery = useInvoices({ status: 'paid' });

  const isLoading =
    allQuery.isLoading || draftQuery.isLoading || sentQuery.isLoading || paidQuery.isLoading;

  const getInvoicesByTab = (): Invoice[] => {
    switch (activeTab) {
      case 'draft':
        return draftQuery.data?.invoices || [];
      case 'sent':
        return sentQuery.data?.invoices || [];
      case 'paid':
        return paidQuery.data?.invoices || [];
      case 'all':
      default:
        return allQuery.data?.invoices || [];
    }
  };

  const invoices = getInvoicesByTab();

  const getTabCount = (tab: TabType): number => {
    switch (tab) {
      case 'draft':
        return draftQuery.data?.total || 0;
      case 'sent':
        return sentQuery.data?.total || 0;
      case 'paid':
        return paidQuery.data?.total || 0;
      case 'all':
      default:
        return allQuery.data?.total || 0;
    }
  };

  // If settings not configured, show setup prompt
  if (!isLoading && !isConfigured) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        <div className="bg-white p-4 border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
        </div>

        <div className="p-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Set Up Invoicing
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Before you can generate invoices, complete your invoice settings. This takes just 2 minutes.
            </p>
            <button
              onClick={() => navigate('/profile?tab=invoice-settings')}
              className="px-6 py-3 bg-[#5B8E87] text-white rounded-lg font-medium hover:bg-[#4A7269] transition-colors"
            >
              Set Up Now
            </button>
          </div>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Invoices</h1>
          <div className="w-5" /> {/* Spacer for alignment */}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {(['all', 'draft', 'sent', 'paid'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[#5B8E87] text-[#5B8E87]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {!isLoading && (
                <span className="ml-2 text-xs">({getTabCount(tab)})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice List */}
      <div className="p-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-[#5B8E87] rounded-full animate-spin" />
            <p className="mt-2 text-sm text-gray-500">Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab !== 'all' ? activeTab : ''} invoices yet
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {activeTab === 'all'
                ? 'Generate your first invoice from the Payments screen'
                : `No ${activeTab} invoices found`}
            </p>
            {activeTab === 'all' && (
              <button
                onClick={() => navigate('/payments')}
                className="px-6 py-3 bg-[#5B8E87] text-white rounded-lg font-medium hover:bg-[#4A7269] transition-colors inline-flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Go to Payments
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
